import { useState } from "react";
import { Activity, Images } from "lucide-react";

import { EmptyState, ExternalLinkText } from "@/components/common";
import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import { trpc } from "@/lib/trpc";
import { StatusBadge } from "@/pages/brands";

import type { BrandPage, BrandStatus } from "./types";

function PageCard({ page }: { page: BrandPage }) {
  const [broken, setBroken] = useState(false);

  const shortUrl = (() => {
    try {
      return new URL(page.url).pathname || "/";
    } catch {
      return page.url;
    }
  })();

  return (
    <div className="group relative flex flex-col gap-2 overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-md">
      <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-muted">
        {broken || !page.screenshotUrl ? (
          <div className="flex flex-col items-center gap-1 p-4 text-xs text-muted-foreground">
            <Images className="size-6 opacity-40" />
            <span>No screenshot</span>
          </div>
        ) : (
          <img
            src={page.screenshotUrl}
            alt={page.url}
            onError={() => setBroken(true)}
            className="h-full w-full object-cover object-top"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-between space-y-1.5 px-3 pb-3">
        <div className="mt-1 flex items-start justify-between gap-2">
          <ExternalLinkText
            href={page.url}
            className="flex items-center gap-1 truncate text-sm font-semibold transition-colors hover:text-blue-600"
            title={page.url}
            truncate
          >
            {shortUrl}
          </ExternalLinkText>
          <StatusBadge status={page.status as BrandStatus} />
        </div>

        <div className="mt-1 flex items-center justify-between border-t pt-1 font-mono text-xs text-muted-foreground">
          <span className="capitalize">{page.pageType}</span>
          {page.httpStatus ? (
            <span className={page.httpStatus >= 400 ? "font-medium text-destructive" : ""}>
              HTTP {page.httpStatus}
            </span>
          ) : null}
        </div>

        {(page.textCharCount ?? 0) > 0 || (page.markdownCharCount ?? 0) > 0 ? (
          <div className="mt-1 flex flex-col gap-1 border-t pt-1 font-mono text-xs text-muted-foreground">
            <div className="flex items-center justify-between">
              <span>Text</span>
              {page.textUrl ? (
                <a
                  href={page.textUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="max-w-[100px] truncate text-blue-600 hover:text-foreground"
                >
                  {page.textCharCount?.toLocaleString()} chars
                </a>
              ) : (
                <span>{page.textCharCount?.toLocaleString()} chars</span>
              )}
            </div>
            <div className="flex items-center justify-between">
              <span>Markdown</span>
              {page.markdownUrl ? (
                <a
                  href={page.markdownUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="max-w-[100px] truncate text-blue-600 hover:text-foreground"
                >
                  {page.markdownCharCount?.toLocaleString()} chars
                </a>
              ) : (
                <span>{page.markdownCharCount?.toLocaleString()} chars</span>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function BrandPagesGallery({ brandId }: { brandId: string }) {
  const { data: pages, isPending, error } = trpc.admin.brands.pages.useQuery({ brandId });
  const PAGE_SIZE = 12;
  const [pageIndex, setPageIndex] = useState(0);

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Scraped Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="aspect-video rounded-md" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Scraped Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-24 items-center justify-center rounded-md border text-sm text-destructive">
            Failed to load pages
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalPages = Math.ceil(pages.length / PAGE_SIZE);
  const visiblePages = pages.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-sm font-medium">
          <span className="flex items-center gap-2">
            <Activity className="size-4 text-muted-foreground" />
            Scraped Pages
          </span>
          <span className="text-xs font-normal text-muted-foreground">
            {pages.length} {pages.length === 1 ? "page" : "pages"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pages.length > 0 ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {visiblePages.map((page) => (
                <PageCard key={page.id} page={page} />
              ))}
            </div>
            {totalPages > 1 ? (
              <div className="flex items-center justify-between pt-2 text-sm text-muted-foreground">
                <span>
                  Page {pageIndex + 1} of {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={pageIndex >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <EmptyState description="No pages have been scraped for this brand yet." />
        )}
      </CardContent>
    </Card>
  );
}
